import { TODO } from '@b2b/types/core';
import { FormCategoryType, FormDisplaySettings, FormsSummariesListItem } from '@b2b/types/forms';
import { v4 as uuidv4 } from 'uuid';

export type OrdersDetailFormsModel = FormsSummariesListItem & {
  membershipId: number;
  membershipLevel: string;
  membershipOffering: string;
  membershipLifecycle: string;
  uuid: string;
  itemNameDisplay: string;
};

export interface FormDisplayOptions {
  parseDate: (date: string) => Date;
  filterDate: (date: Date, format: string) => string | null;
}

/**
 * Minimal shape of an event and its order form as returned by ApiService.event.detail().
 * Used to map org-order forms (which have no saved response yet) into OrdersDetailFormsModel.
 */
export interface OrgOrderEventFormInput {
  id: string;
  name: string;
  formCategoryType: FormCategoryType;
  formDisplaySettings?: FormDisplaySettings;
}

export interface OrgOrderEventInput {
  id: string;
  name: string;
  startTime: string;
}

/**
 * Maps a raw event order form (from ApiService.event.detail) into an OrdersDetailFormsModel
 * suitable for display in the orders-detail forms grid.
 *
 * Uses `form.id` as the row `id` so that deduplicateFormsByDisplaySettings can key on it.
 * `formResponseId` is intentionally empty — these forms have no response yet.
 */
export function buildOrgOrderFormModel(
  event: OrgOrderEventInput,
  form: OrgOrderEventFormInput,
  orderId: number,
  customerId: string,
): OrdersDetailFormsModel {
  return {
    uuid: uuidv4(),
    id: form.id,
    orderId: String(orderId),
    itemType: 'Event',
    itemId: event.id,
    itemName: event.name,
    itemNameDisplay: '',
    eventStartTime: event.startTime,
    formId: form.id,
    formName: form.name,
    formNumber: '',
    formResponseId: '',
    formCategoryType: form.formCategoryType,
    formDisplaySettings: form.formDisplaySettings,
    customerId,
    membershipId: 0,
    membershipLevel: '',
    membershipOffering: '',
    membershipLifecycle: '',
  };
}

/**
 * Builds the `relatedItemNames` array shown in the form fillout modal header.
 * Combines the item name with the formatted event start date/time when available.
 */
export function buildRelatedItemNames(form: OrdersDetailFormsModel, options: FormDisplayOptions): string[] {
  const parts: string[] = [];

  if (form.itemNameDisplay) {
    return [form.itemNameDisplay];
  }

  if (form.itemName) {
    parts.push(form.itemName);
  }

  if (form.eventStartTime) {
    const parsed = options.parseDate(form.eventStartTime);
    const displayDate = options.filterDate(parsed, 'EEEE, MMM d, yyyy') ?? '';
    const displayTime = options.filterDate(parsed, 'h:mm a') ?? '';
    parts.push(`${displayDate} ${displayTime}`.trim());
  }

  return parts.length ? [parts.join(' - ')] : [];
}

/**
 * Builds the data object passed to CartFormsService.openFormFilloutModalWithData.
 */
export function buildModalData(
  form: OrdersDetailFormsModel,
  options: FormDisplayOptions,
  existingResponse?: TODO,
): TODO {
  return {
    form: {
      id: form.formId,
      itemType: form.formCategoryType,
      ...(form.formCategoryType === 'membership'
        ? { membershipName: form.membershipLevel || '' }
        : { event: { name: form.itemName } }),
    } as TODO,
    existingResponse,
    relatedItemNames: buildRelatedItemNames(form, options),
    skipSaveInSessionStorage: true,
  };
}

/**
 * Maps the current grid model into the PUT payload for the orders forms update endpoint.
 * The row being saved gets the full new response; all other rows keep their existing response reference.
 */
export function buildUpdateItems(form: OrdersDetailFormsModel, response: TODO, model: OrdersDetailFormsModel[]) {
  return model
    .map((item): TODO => {
      const isUpdatedItem = item.uuid === form.uuid;

      if (isUpdatedItem) {
        return { ...item, responseForms: [response] };
      }

      return item;
    })
    .filter((item) => item.responseForms && item.responseForms.length > 0);
}

export function deduplicateOrderFormsByDisplaySettings(orgOrderFormsPerEvent: OrdersDetailFormsModel[]): OrdersDetailFormsModel[] {
  const seen = new Map<string, OrdersDetailFormsModel & { relatedItemNames: string[] }>();
  orgOrderFormsPerEvent.forEach((form) => {
    if (form.formDisplaySettings === 'perCart') {
      if (!seen.has(form.id)) {
        seen.set(form.id, { ...form, relatedItemNames: [form.itemName] });
      } else {
        const existing = seen.get(form.id);
        if (existing && form.itemName && !existing.relatedItemNames.includes(form.itemName)) {
          existing.relatedItemNames.push(form.itemName);
          seen.set(form.id, { ...existing });
        }
      }
    } else if (form.formDisplaySettings === 'perItem') {
      seen.set(`${form.id}-${seen.size}`, { ...form, relatedItemNames: [form.itemName] });
    } else {
      seen.set(form.id, { ...form, relatedItemNames: [form.itemName] });
    }
  });
  
  return Array.from(seen.values());
}
